import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, RefreshCw } from "lucide-react";
import BoostPremiumButton from "@/components/BoostPremiumButton";

export default function MyListings() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/properties/mine").then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return <div className="py-20 text-center text-muted-foreground">{t.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/my-listings" }} replace />;

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/properties/${id}`);
      setItems((it) => it.filter((p) => p.id !== id));
      toast.success("Listing deleted");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const extend = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/extend`);
      setItems((it) => it.map((p) => p.id === id ? { ...p, expires_at: data.expires_at, status: "active" } : p));
      toast.success("Extended by 60 days");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const daysToExpiry = (iso) => {
    if (!iso) return null;
    const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
    return d;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-secondary">Dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t.nav.myListings}</h1>
        </div>
        <Link to="/sell">
          <Button className="bg-primary hover:bg-primary/90 rounded-full"><Plus size={14} className="mr-1" />{t.nav.sell}</Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">{t.common.loading}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl bg-accent/30" data-testid="my-listings-empty">
          <p className="text-lg font-medium">Aapne abhi tak koi listing nahi banayi</p>
          <Link to="/sell" className="text-primary underline text-sm mt-1 inline-block">Create your first listing</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <div key={p.id} className="relative">
              <PropertyCard property={p} index={i} />
              <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
                <Link to={`/edit-property/${p.id}`}>
                  <button
                    className="w-8 h-8 grid place-items-center bg-white/90 hover:bg-primary hover:text-primary-foreground rounded-full shadow"
                    data-testid={`edit-btn-${p.id}`}
                    aria-label="edit"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Pencil size={13} />
                  </button>
                </Link>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(p.id); }}
                  className="w-8 h-8 grid place-items-center bg-white/90 hover:bg-destructive hover:text-destructive-foreground rounded-full shadow"
                  data-testid={`delete-btn-${p.id}`}
                  aria-label="delete"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="mt-2 px-1 space-y-1.5">
                {(() => {
                  const d = daysToExpiry(p.expires_at);
                  if (d == null) return null;
                  if (d <= 0) return <div className="text-[10px] text-destructive font-semibold" data-testid={`expiry-${p.id}`}>⏱ Expired {-d} days ago</div>;
                  if (d <= 7) return <div className="text-[10px] text-amber-700 font-semibold" data-testid={`expiry-${p.id}`}>⏱ Expires in {d} days</div>;
                  return <div className="text-[10px] text-muted-foreground" data-testid={`expiry-${p.id}`}>Expires in {d} days</div>;
                })()}
                <div className="flex gap-1.5">
                  <Select
                    value={p.status || "active"}
                    onValueChange={async (v) => {
                      try {
                        await api.patch(`/properties/${p.id}/status`, { status: v });
                        setItems((it) => it.map((x) => x.id === p.id ? { ...x, status: v } : x));
                        toast.success(`Status: ${v.replace("_", " ")}`);
                      } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid={`status-select-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="under_negotiation">Under Negotiation</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="rented">Rented</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs px-2"
                    onClick={() => extend(p.id)}
                    data-testid={`extend-btn-${p.id}`}
                    title="Extend by 60 days"
                  >
                    <RefreshCw size={11} className="mr-1" />+60d
                  </Button>
                </div>
                <div className="pt-1">
                  <BoostPremiumButton
                    property={p}
                    onBoosted={(v) => setItems((it) => it.map((x) => x.id === p.id ? { ...x, is_premium: v.is_premium, premium_plan: v.premium_plan, premium_expires_at: v.premium_expires_at } : x))}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
